import React from 'react';
import {
  AdManagerLogo,
  AdvisorLogo,
  AtlasLogo,
  BeaconLogo,
  BrandclubOSLogo,
  BrowserRegistryLogo,
  DigitalShelfLogo,
  DiscoverLogo,
  OmniLogo,
  ShopperAnalyticsLogo,
  ShopperosLogo,
  SyncLogo
} from 'src/components/SvgIcons/SvgIcons';

export interface AppCatalogEntry {
  /** Display name shown to users. */
  name: string;
  /** Brand color. Historical — see the note on APP_CONFIG below. */
  color: string;
  /** Monogram fallback for surfaces that have no room for a mark. */
  letter: string;
  /** The product mark. Rendered inline from the SVG sprite. */
  Logo?: React.ComponentType<{ style?: React.CSSProperties; className?: string }>;
  /**
   * One line on what the product does, for surfaces that have room. Taken from
   * stackline.com's own product descriptions, except Digital Shelf and
   * Brandclub OS, which have no public copy — those two are drafts.
   */
  blurb?: string;
}

/**
 * The one place that knows what an app is called and what it looks like.
 *
 * Keyed by the `appAccess` key the API returns on a division membership, which
 * is also the bare app name a referrer normalizes to. Previously duplicated as
 * a local const inside SwitchDivision.tsx; keep it here so a new app is taught
 * to every surface at once.
 *
 * `color` and `letter` are carried forward unchanged from that original map so
 * existing consumers are untouched. Note the colors are Material palette
 * defaults rather than Stackline brand values, and sit awkwardly next to the
 * product marks — prefer `Logo` on any new surface.
 */
export const APP_CONFIG: Record<string, AppCatalogEntry> = {
  atlas: { name: 'Atlas', color: '#1976d2', letter: 'A', Logo: AtlasLogo, blurb: 'Competitive intelligence across Amazon, Walmart and Target — ad spend, market share, product performance.' },
  beacon: { name: 'Beacon', color: '#2e7d32', letter: 'B', Logo: BeaconLogo, blurb: 'AI analytics: 52-week SKU-level forecasts, scenario modeling and growth recommendations.' },
  advertising: { name: 'Ad manager', color: '#9c27b0', letter: 'Ad', Logo: AdManagerLogo, blurb: 'Retail media at enterprise scale — automated pacing, targeting and real-time bid optimization.' },
  ad: { name: 'Ad manager', color: '#9c27b0', letter: 'Ad', Logo: AdManagerLogo, blurb: 'Retail media at enterprise scale — automated pacing, targeting and real-time bid optimization.' },
  admanager: { name: 'Ad manager', color: '#9c27b0', letter: 'AM', Logo: AdManagerLogo, blurb: 'Retail media at enterprise scale — automated pacing, targeting and real-time bid optimization.' },
  advisor: { name: 'Advisor', color: '#1a1a2e', letter: 'Av', Logo: AdvisorLogo, blurb: 'An AI retail expert that researches your categories and returns action plans with forecast ROI.' },
  // `omni` predates the Shopper Analytics rename and still arrives as its own
  // appAccess key. It keeps its own mark; the launcher de-duplicates by display
  // name so a division holding both does not list Shopper Analytics twice.
  omni: { name: 'Shopper Analytics', color: '#f57c00', letter: 'SA', Logo: OmniLogo, blurb: 'Find high-value audience segments and track shopper behaviour across retailers.' },
  shopperanalytics: { name: 'Shopper Analytics', color: '#f57c00', letter: 'SA', Logo: ShopperAnalyticsLogo, blurb: 'Find high-value audience segments and track shopper behaviour across retailers.' },
  shopperos: { name: 'Shopper OS', color: '#5CC4BE', letter: 'SO', Logo: ShopperosLogo, blurb: 'Connect with, engage and retain shoppers across the whole retail ecosystem.' },
  discover: { name: 'Digital Shelf', color: '#00bcd4', letter: 'DS', Logo: DiscoverLogo, blurb: 'Watch how your products show up across every retailer you sell on.' },
  digitalshelf: { name: 'Digital Shelf', color: '#00bcd4', letter: 'DS', Logo: DigitalShelfLogo, blurb: 'Watch how your products show up across every retailer you sell on.' },
  'digital-shelf': { name: 'Digital Shelf', color: '#00bcd4', letter: 'DS', Logo: DigitalShelfLogo, blurb: 'Watch how your products show up across every retailer you sell on.' },
  'browser-registry': { name: 'Browser Registry', color: '#031425', letter: 'BR', Logo: BrowserRegistryLogo },
  brandclubos: { name: 'Brandclub OS', color: '#181d56', letter: 'BC', Logo: BrandclubOSLogo, blurb: 'Shopper engagement and rewards, under the Brandclub brand.' },
  sync: { name: 'Sync', color: '#031425', letter: 'Sy', Logo: SyncLogo }
};

/**
 * Apps that run their own authorization layer and are therefore NOT represented
 * in `sl-company-division.appAccess` at all -- a scan of all 3,347 prod
 * divisions finds none of these keys anywhere in it. Gating them on appAccess
 * would hide them from every user forever, so the launcher always offers them
 * and lets the app itself decide.
 *
 * Same set, same reasoning as AppReferrer.SelfAuthorizingApps on the API side.
 */
export const ALWAYS_AVAILABLE_APPS: readonly string[] = ['brandclubos', 'shopperos', 'shopperanalytics'];

/** Catalog entry for an appAccess key, with a safe fallback for unknown apps. */
export const getAppEntry = (appKey: string): AppCatalogEntry =>
  APP_CONFIG[appKey] || { name: appKey, color: '#666', letter: '?' };

/**
 * True when a division's appAccess grants a usable level for `appKey`.
 * Mirrors DivisionHasAppAccess in EnterpriseAuthService: present, non-empty,
 * and not explicitly "blocked".
 */
export const hasAppAccess = (appAccess: Record<string, string> | undefined, appKey: string): boolean => {
  const level = appAccess?.[appKey];
  return !!level && level.toLowerCase() !== 'blocked';
};

/**
 * The apps a division can actually open, de-duplicated by display name so
 * aliased keys (omni/shopperanalytics, discover/digitalshelf, ad/advertising/
 * admanager) surface once.
 */
export const openableApps = (
  appAccess: Record<string, string> | undefined,
  options: { include?: readonly string[] } = {}
): Array<AppCatalogEntry & { key: string }> => {
  const seen = new Set<string>();
  const granted = Object.keys(appAccess || {}).filter((key) => hasAppAccess(appAccess, key));
  // `include` apps are offered whether or not appAccess mentions them.
  return [...new Set([...granted, ...(options.include || [])])]
    .filter((key) => APP_CONFIG[key])
    .map((key) => ({ key, ...APP_CONFIG[key] }))
    .filter((entry) => {
      if (seen.has(entry.name)) return false;
      seen.add(entry.name);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};
